import type { TreeNode, TreeNodeState } from '../Editor';

/**
 * Generate a UUID.
 */
export const treeNodeUUID = () => {
  return crypto.randomUUID();
};

/**
 * Find a specific node by its id and return parent node which contains the final node.
 */
export function getNodePathById(id: string | undefined | null, nodes: Map<string, TreeNode>): TreeNode | null {
  if (!id) {
    return null;
  }

  // recursively search for the node
  for (const node of nodes.values()) {
    if (node.id === id) {
      return {
        ...node,
        childrenNodes: new Map(),
      };
    }

    if (node?.childrenNodes?.size > 0) {
      const child = getNodePathById(id, node.childrenNodes);
      if (child) {
        const childrenNodes = new Map();
        childrenNodes.set(child.id, child);
        return {
          ...node,
          childrenNodes: childrenNodes,
        };
      }
    }
  }

  return null;
}

/**
 * Get a specific node by its id.
 */
export const getNodeById = <NS extends TreeNodeState = TreeNodeState>(
  id: string | null | undefined,
  nodes: Map<string, TreeNode>
): TreeNode<NS> | null => {
  if (!id) {
    return null;
  }

  // recursively search for the node
  for (const node of nodes.values()) {
    if (node.id === id) {
      return node as TreeNode<NS>;
    }

    if (node?.childrenNodes) {
      const child = getNodeById(id, node.childrenNodes);
      if (child) {
        return child as TreeNode<NS>;
      }
    }
  }

  return null;
};

/**
 * Traverse the nodes and return the nodes which satisfy the predicate.
 */
const nodeTraversal = (
  predicate: (node: TreeNode) => boolean,
  nodes: Map<string, TreeNode>,
  results: TreeNode[] = []
): TreeNode[] => {
  for (const node of nodes.values()) {
    if (predicate(node)) {
      results.push(node);
    }

    if (node?.childrenNodes) {
      nodeTraversal(predicate, node.childrenNodes, results);
    }
  }

  return results;
};

/**
 * Filter the nodes based on the predicate.
 */
export const filterNodes = (predicate: (node: TreeNode) => boolean, nodes: Map<string, TreeNode>): TreeNode[] => {
  return nodeTraversal(predicate, nodes);
};
